import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import axios from "axios";
import { set } from "lodash";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";
import * as creators from "../actions/creators";
import getProvinceObject from "../helpers";

const getDefaultOfficeModel = () => ({
  name: "",
  phone: "",
  fax: "",
  address_attributes: {
    street_address: "",
    city: "",
    locality: "",
    postal_code: "",
    country: "",
  },
});

const NewOfficeModal = ({ addOfficeSuccess, onClose, open, states, territories }) => {
  const [loading, setLoading] = useState(false);
  const [formData, _setFormData] = useState(getDefaultOfficeModel());

  const setFormData = (e) => {
    const { name, value } = e.target;
    _setFormData((p) => set({ ...p }, name, value));
  };

  useEffect(() => {
    if (open) _setFormData(getDefaultOfficeModel());
  }, [open]);

  return (
    <Modal
      show={open}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);
          axios
            .patch(
              "/api/v4/business_settings",
              {
                account: { account_offices_attributes: [formData] },
              },
              { headers: { "x-csrf-token": window.ReactOnRails.authenticityToken() } },
            )
            .then((res) => {
              setLoading(false);
              addOfficeSuccess({ data: res.data });
              onClose();
            })
            .catch((err) => {
              setLoading(false);
              console.error(err);
            });
        }}
        className="tw-flex tw-flex-col tw-gap-[32px]"
      >
        <Modal.Header title="Add Office" closeButton={!loading} />

        <Modal.Body>
          <fieldset disabled={loading} className="tw-flex tw-flex-col tw-gap-[24px]">
            <TextInput
              name="name"
              value={formData.name}
              onChange={setFormData}
              label="Office Name"
              isRequired
            />
            <div className="tw-flex tw-gap-[24px]">
              <TextInput
                name="phone"
                containerClassName="tw-flex-1"
                value={formData.phone}
                onChange={setFormData}
                label="Phone"
              />
              <TextInput
                name="fax"
                containerClassName="tw-flex-1"
                value={formData.fax}
                onChange={setFormData}
                label="Fax"
              />
            </div>
            <TextInput
              name="address_attributes.street_address"
              value={formData.address_attributes.street_address}
              onChange={setFormData}
              label="Street Address"
              isRequired
            />
            <div className="tw-flex tw-gap-[24px]">
              <TextInput
                name="address_attributes.city"
                containerClassName="tw-flex-1"
                value={formData.address_attributes.city}
                onChange={setFormData}
                label="City"
              />
              <Dropdown
                name="address_attributes.locality"
                containerClassName="tw-flex-1 tw-min-w-[108px]"
                isSearchable
                label="State / Province"
                placeholder="Select"
                value={getProvinceObject(states, territories, formData.address_attributes.locality)}
                onChange={({ value }, { name }) => setFormData({ target: { name, value } })}
                options={[
                  {
                    label: "States",
                    options: states,
                  },
                  {
                    label: "Province",
                    options: territories,
                  },
                ]}
              />
              <TextInput
                name="address_attributes.postal_code"
                containerClassName="tw-flex-1 tw-overflow-hidden"
                value={formData.address_attributes.postal_code}
                onChange={setFormData}
                label="ZIP / Postal Code"
              />
            </div>
            <TextInput
              name="address_attributes.country"
              value={formData.address_attributes.country}
              onChange={setFormData}
              label="Country"
            />
          </fieldset>
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          <Button onClick={onClose} disabled={loading} size="medium" schema="tertiary">
            Cancel
          </Button>
          <Button
            isLoading={loading}
            type="submit"
            disabled={formData.name <= "" || formData.address_attributes.street_address <= ""}
            size="medium"
          >
            Add
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

NewOfficeModal.propTypes = {
  addOfficeSuccess: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  territories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  states: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const mapStateToProps = (state) => ({
  model: state.businessSettings.newOfficeModel,
  territories: state.businessSettings.territories,
  states: state.businessSettings.states,
});

export default connect(mapStateToProps, creators)(NewOfficeModal);
